import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Paper,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as PdfIcon,
  VideoLibrary as VideoIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Course, Video } from '../types';

interface QuickRecapModalProps {
  open: boolean;
  onClose: () => void;
  itemTitle: string;
  courses: Course[];
  videos: Video[];
  selectedCourseId?: number | null;
  selectedVideoId?: number | null;
  onSaveCourse: (courseId: number | null) => Promise<void>;
  onSaveVideo: (videoId: number | null) => Promise<void>;
}

const QuickRecapModal: React.FC<QuickRecapModalProps> = ({
  open,
  onClose,
  itemTitle,
  courses,
  videos,
  selectedCourseId,
  selectedVideoId,
  onSaveCourse,
  onSaveVideo,
}) => {
  const [tempCourse, setTempCourse] = useState<Course | null>(
    courses.find(c => c.id === selectedCourseId) || null
  );
  const [tempVideo, setTempVideo] = useState<Video | null>(
    videos.find(v => v.id === selectedVideoId) || null
  );
  const [isSaving, setIsSaving] = useState(false);

  // Update temp values when modal opens with new values
  React.useEffect(() => {
    if (open) {
      setTempCourse(courses.find(c => c.id === selectedCourseId) || null);
      setTempVideo(videos.find(v => v.id === selectedVideoId) || null);
    }
  }, [open, selectedCourseId, selectedVideoId, courses, videos]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save both course and video
      if (tempCourse?.id !== selectedCourseId) {
        await onSaveCourse(tempCourse?.id || null);
      }
      if (tempVideo?.id !== selectedVideoId) {
        await onSaveVideo(tempVideo?.id || null);
      }
      onClose();
    } catch (error) {
      console.error('Error saving quick recaps:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Quick Recaps - {itemTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Course Quick Recap Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              border: '1px solid',
              borderColor: tempCourse ? 'success.main' : 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PdfIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Cours PDF
              </Typography>
              {tempCourse && (
                <Chip
                  label="Assigné"
                  color="success"
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              )}
            </Box>

            <Autocomplete
              options={courses}
              getOptionLabel={(option) => option.title}
              value={tempCourse}
              onChange={(_, newValue) => setTempCourse(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sélectionner un cours"
                  placeholder="Rechercher un cours..."
                  variant="outlined"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>
                    {option.tags && option.tags.length > 0 && (
                      <Box display="flex" gap={0.5} mt={0.5}>
                        {option.tags.slice(0, 3).map((tag, i) => (
                          <Chip key={i} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </Box>
                </li>
              )}
            />

            {tempCourse && (
              <Box mt={2}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        {tempCourse.title}
                      </Typography>
                      {tempCourse.tags && tempCourse.tags.length > 0 && (
                        <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                          {tempCourse.tags.map((tag, i) => (
                            <Chip key={i} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setTempCourse(null)}
                      color="error"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Box>
            )}
          </Paper>

          <Divider sx={{ my: 2 }} />

          {/* Video Quick Recap Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'rgba(156, 39, 176, 0.04)',
              border: '1px solid',
              borderColor: tempVideo ? 'success.main' : 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <VideoIcon color="secondary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Vidéo
              </Typography>
              {tempVideo && (
                <Chip
                  label="Assigné"
                  color="success"
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              )}
            </Box>

            <Autocomplete
              options={videos}
              getOptionLabel={(option) => option.title}
              value={tempVideo}
              onChange={(_, newValue) => setTempVideo(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sélectionner une vidéo"
                  placeholder="Rechercher une vidéo..."
                  variant="outlined"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary">
                        {option.description.substring(0, 100)}
                        {option.description.length > 100 ? '...' : ''}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
            />

            {tempVideo && (
              <Box mt={2}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        {tempVideo.title}
                      </Typography>
                      {tempVideo.description && (
                        <Typography variant="caption" color="text.secondary">
                          {tempVideo.description}
                        </Typography>
                      )}
                      {tempVideo.tags && tempVideo.tags.length > 0 && (
                        <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                          {tempVideo.tags.map((tag, i) => (
                            <Chip key={i} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setTempVideo(null)}
                      color="error"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Box>
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
        >
          {isSaving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickRecapModal;
